import { withWaterSourceTruthPolicy } from './waterSourcePolicy';
import { withAirAttainmentTruthPolicy } from './airAttainmentPolicy';
import { withRegulatoryFeasibilityPolicy } from './regulatoryFeasibilityPolicy';
import { withCurrentTruthReadinessPolicy } from './currentTruthReadinessPolicy';
import { REPORT_PROMPT_VERSIONS } from '../../config/reportPromptVersions';
import type { TestFitAuthorityRef } from '../../types/generationImpact';
import type { JsonValue } from '../../types/reportContractsV2';
import {
  DECISION_ANALYSIS_HASH_VERSION,
  DECISION_ANALYSIS_HASH_VERSION_V2,
  DECISION_ANALYSIS_INPUT_SCHEMA_VERSION,
  DECISION_ANALYSIS_SCHEMA_VERSION,
  decisionAnalysisInputEnvelopeV1Schema,
  decisionAnalysisSemanticContentV1Schema,
  decisionAnalysisSnapshotV1Schema,
  decisionAnalysisUpstreamReferenceV1Schema,
  type DecisionAnalysisAuthorityV1,
  type DecisionAnalysisDomain,
  type DecisionAnalysisInputEnvelopeV1,
  type DecisionAnalysisSemanticContentV1,
  type DecisionAnalysisSnapshotV1,
  type DecisionAnalysisUpstreamReferenceV1,
  type DecisionAnalysisVersionEnvelopeV1,
} from '../../types/decisionAnalysis';
import {
  canonicalJson,
  hashCanonicalJson,
} from '../reportGeneration/reportContractHash';
import {
  boundSectionUnchanged,
  boundSedSectionsForDomain,
  buildReportSedMaterialDependencyManifest,
  computeSedContentHashV2,
  computeSedSectionContentHashesV2,
  computeSedSectionContentHashesV3,
  reportSedMaterialDependencyDrift,
  verifyReportSedMaterialDependencyManifest,
} from './sedContentIdentity';
import { boundTypedPreferencesMatch } from './typedPreferenceCurrency';
import defaultScoringThresholds from '../../config/scoringThresholds.json';

export const DEFAULT_DECISION_ANALYSIS_VERSIONS = Object.freeze({
  analysisPolicy: 'decision-analysis-policy-v1',
  factModel: 'decision-analysis-fact-model-v1',
  scorer: defaultScoringThresholds.version,
}) satisfies DecisionAnalysisVersionEnvelopeV1;

export interface BuildDecisionAnalysisInputEnvelopeV1Args {
  siteId: string;
  domain: DecisionAnalysisDomain;
  sed: {
    id: string;
    version: number;
    /** Exact SED JSON that the analysis consumes. */
    document: unknown;
  };
  /** Undefined/null means the project intent is explicitly absent. */
  intentSnapshot?: unknown | null;
  /** Optional site-config revision stamp; the content hash remains authoritative. */
  intentRevision?: string | null;
  /** Undefined/null means no domain preferences were applied. */
  typedPreferencesSnapshot?: unknown | null;
  powerStrategy?: 'grid-only' | 'btm-only' | 'hybrid' | 'islanded' | null;
  authority?: DecisionAnalysisAuthorityV1 | TestFitAuthorityRef | null;
  versions?: Partial<DecisionAnalysisVersionEnvelopeV1>;
  upstream?: readonly DecisionAnalysisUpstreamReferenceV1[];
  materialScope?: 'report-prompt' | 'full-document';
  /** Only replaying an unstamped historical source uses the legacy policy. */
  bindMaterialDependencies?: boolean;
}

export interface BuildDecisionAnalysisUpstreamReferenceV1Args {
  kind: string;
  id: string;
  version?: string | null;
  /** Exact simulation/layout/boundary/provider payload consumed by analysis. */
  content: unknown;
  materialIdentity?: DecisionAnalysisUpstreamReferenceV1['materialIdentity'];
}

export interface BuildDecisionAnalysisSnapshotV1Args {
  input: DecisionAnalysisInputEnvelopeV1;
  createdAt: string;
  semantic: DecisionAnalysisSemanticContentV1;
}

export interface DecisionAnalysisSnapshotIdentityCheck {
  valid: boolean;
  expectedInputHash: string | null;
  expectedContentHash: string | null;
  expectedAnalysisId: string | null;
  reasons: string[];
}

export interface DecisionAnalysisInputIdentityCheck {
  valid: boolean;
  input: DecisionAnalysisInputEnvelopeV1 | null;
  inputHash: string | null;
  reasons: string[];
}

function toJsonValue(value: unknown): JsonValue {
  return JSON.parse(canonicalJson(value)) as JsonValue;
}

function resolveAuthority(
  authority: BuildDecisionAnalysisInputEnvelopeV1Args['authority'],
): DecisionAnalysisAuthorityV1 {
  if (!authority) return { kind: 'none' };
  return 'kind' in authority
    ? structuredClone(authority)
    : { kind: 'test-fit', ref: structuredClone(authority) };
}

function upstreamSortKey(reference: DecisionAnalysisUpstreamReferenceV1): string {
  return [
    reference.kind,
    reference.id,
    reference.version ?? '',
    reference.contentHash,
  ].join('\u0000');
}

/** Treat upstream references as an identity set, not caller-order-dependent UI data. */
export function normalizeDecisionAnalysisUpstreamReferences(
  references: readonly DecisionAnalysisUpstreamReferenceV1[],
): DecisionAnalysisUpstreamReferenceV1[] {
  return references
    .map(reference => decisionAnalysisUpstreamReferenceV1Schema.parse(reference))
    .sort((left, right) => upstreamSortKey(left).localeCompare(upstreamSortKey(right)));
}

export function buildDecisionAnalysisUpstreamReferenceV1(
  input: BuildDecisionAnalysisUpstreamReferenceV1Args,
): DecisionAnalysisUpstreamReferenceV1 {
  return decisionAnalysisUpstreamReferenceV1Schema.parse({
    kind: input.kind,
    id: input.id,
    version: input.version ?? null,
    contentHash: hashCanonicalJson(input.content),
    ...(input.materialIdentity ? { materialIdentity: input.materialIdentity } : {}),
  });
}

/**
 * Build the only supported analysis-input identity from live state. Hashing
 * the exact SED, intent and typed-preference snapshots here prevents callers
 * from duplicating or weakening currentness logic.
 */
export function buildDecisionAnalysisInputEnvelopeV1(
  args: BuildDecisionAnalysisInputEnvelopeV1Args,
): DecisionAnalysisInputEnvelopeV1 {
  const intentSnapshot = args.intentSnapshot == null
    ? null
    : toJsonValue(args.intentSnapshot);
  const typedPreferencesSnapshot = args.typedPreferencesSnapshot == null
    ? null
    : toJsonValue(args.typedPreferencesSnapshot);
  const boundSections = boundSedSectionsForDomain(args.domain);
  const versions: DecisionAnalysisVersionEnvelopeV1 = {
    ...DEFAULT_DECISION_ANALYSIS_VERSIONS,
    ...(args.versions ?? {}),
    analysisPolicy: withWaterSourceTruthPolicy(args.domain, withRegulatoryFeasibilityPolicy(args.domain,
      withAirAttainmentTruthPolicy(args.domain, withCurrentTruthReadinessPolicy(
        !args.versions?.analysisPolicy || args.versions.analysisPolicy === DEFAULT_DECISION_ANALYSIS_VERSIONS.analysisPolicy
          ? `${DEFAULT_DECISION_ANALYSIS_VERSIONS.analysisPolicy}:${REPORT_PROMPT_VERSIONS[args.domain]}`
          : args.versions.analysisPolicy)))),
  };

  return decisionAnalysisInputEnvelopeV1Schema.parse({
    schemaVersion: DECISION_ANALYSIS_INPUT_SCHEMA_VERSION,
    // v2 — content-addressed SED identity. `sed.contentHash` is the hash of
    // the NORMALIZED document (rebuild identity + provenance stamps stripped,
    // see ./sedContentIdentity), and the v2 input-hash payload keys the SED
    // by that content hash alone, so a rebuild that reproduces identical
    // evidence keeps existing analyses decision-current. id/version stay on
    // the envelope as provenance.
    hashVersion: DECISION_ANALYSIS_HASH_VERSION_V2,
    siteId: args.siteId,
    domain: args.domain,
    sed: {
      id: args.sed.id,
      version: args.sed.version,
      contentHash: computeSedContentHashV2(args.sed.document),
      ...(args.bindMaterialDependencies === false ? {} : {
        materialDependencies: buildReportSedMaterialDependencyManifest(args.sed.document, args.domain,
          { fullDocument: args.materialScope === 'full-document' }),
      }),
      // Section-scoped staleness metadata (NOT identity — the v2 input-hash
      // payload projects `sed` to `{ contentHash }`). Binding the sections
      // this domain reads lets commit-time currency ignore SED changes in
      // sections the analysis never consumed, which is what stops concurrent
      // runs from staling each other. Empty resolution ⇒ field omitted ⇒
      // whole-document comparison (conservative).
      ...(boundSections.length > 0
        ? {
            sectionHashes: computeSedSectionContentHashesV2(
              args.sed.document,
              boundSections,
            ),
            // Scheme v3 beside v2: cache/provenance labels (" (cached)",
            // carry-forward times, sentiment scan history) stop reading as
            // drift once the live side carries v3 too.
            sectionHashesV3: computeSedSectionContentHashesV3(
              args.sed.document,
              boundSections,
            ),
          }
        : {}),
    },
    intent: intentSnapshot === null
      ? {
          state: 'absent',
          contentHash: hashCanonicalJson(null),
          revision: args.intentRevision ?? null,
          snapshot: null,
        }
      : {
          state: 'present',
          contentHash: hashCanonicalJson(intentSnapshot),
          revision: args.intentRevision ?? null,
          snapshot: intentSnapshot,
        },
    typedPreferences: typedPreferencesSnapshot === null
      ? {
          state: 'absent',
          contentHash: hashCanonicalJson(null),
          snapshot: null,
        }
      : {
          state: 'present',
          contentHash: hashCanonicalJson(typedPreferencesSnapshot),
          snapshot: typedPreferencesSnapshot,
        },
    decisionContext: {
      powerStrategy: args.powerStrategy ?? null,
    },
    authority: resolveAuthority(args.authority),
    versions,
    upstream: normalizeDecisionAnalysisUpstreamReferences(args.upstream ?? []),
  });
}

/**
 * Canonical input payload. Snapshot object key order and upstream-reference
 * insertion order cannot change the identity. The payload derivation follows
 * the envelope's OWN hashVersion so persisted v1 snapshots keep verifying
 * against their stored inputHash:
 *   - v1: the full parsed envelope (sed id + version + raw-document hash all
 *     participate — every rebuild re-keys the identity);
 *   - v2: the SED participates via its normalized CONTENT hash only; id and
 *     version remain on the envelope as provenance but are excluded from
 *     identity, so a content-identical rebuild does not change the hash.
 */
export function decisionAnalysisInputHashPayload(
  input: DecisionAnalysisInputEnvelopeV1,
): Record<string, unknown> {
  const parsed = decisionAnalysisInputEnvelopeV1Schema.parse(input);
  const normalized = {
    ...parsed,
    upstream: normalizeDecisionAnalysisUpstreamReferences(parsed.upstream),
  };
  if (parsed.hashVersion === DECISION_ANALYSIS_HASH_VERSION_V2) {
    return { ...normalized, sed: { contentHash: parsed.sed.contentHash,
      ...(parsed.sed.materialDependencies ? { materialDependencies: parsed.sed.materialDependencies } : {}) } };
  }
  return normalized;
}

export function hashDecisionAnalysisInputV1(
  input: DecisionAnalysisInputEnvelopeV1,
): string {
  return hashCanonicalJson(decisionAnalysisInputHashPayload(input));
}

/**
 * Cross-version projection for comparing a frozen envelope against a live one
 * when they were hashed under DIFFERENT schemes: the v1-shaped payload minus
 * `sed.contentHash` (the two sides computed it under incompatible schemes)
 * with the hashVersion field pinned to a sentinel. SED identity is carried by
 * (id, version) equality — exactly the v1 semantics minus raw-byte
 * sensitivity — so existing v1 snapshots neither flip stale at deploy nor
 * gain content-addressed currency until they are naturally refreshed as v2.
 */
function decisionAnalysisCompatInputHashPayload(
  input: DecisionAnalysisInputEnvelopeV1,
): Record<string, unknown> {
  const parsed = decisionAnalysisInputEnvelopeV1Schema.parse(input);
  return {
    ...parsed,
    hashVersion: 'compat-cross-version',
    sed: { id: parsed.sed.id, version: parsed.sed.version },
    upstream: normalizeDecisionAnalysisUpstreamReferences(parsed.upstream),
  };
}

/**
 * THE currency predicate: is the frozen analysis input still the live input?
 * Same hashVersion on both sides ⇒ plain identity-hash equality (v2 gets
 * content-addressed SED currency). Mixed versions (a v1 snapshot evaluated
 * against a v2 live envelope) ⇒ the compat projection above.
 */
/**
 * Structured drift between a frozen analysis input and the live envelope.
 *
 * `current` is the verdict; `changedBoundSections` names the SED sections the
 * analysis BINDS whose content differs, so callers can say WHAT went stale
 * rather than "something changed".
 */
export type DecisionAnalysisDriftedInput =
  | 'sed'
  | 'intent'
  | 'typed-preferences'
  | 'power-strategy'
  | 'authority'
  | 'analysis-versions'
  | `upstream:${string}`;

export interface DecisionAnalysisInputDrift {
  current: boolean;
  /** Bound SED sections whose content hash differs. Empty when SED-current. */
  changedBoundSections: string[];
  /**
   * True when the ONLY drift is SED content in sections this analysis never
   * binds — the case that must NOT stale the analysis.
   */
  unboundSedChangeOnly: boolean;
  /**
   * Best-effort names of the non-SED inputs whose content differs, so stale
   * reasons can say WHAT drifted ("the project intent changed") instead of
   * listing six suspects (live 2026-08-17: "SED v1 · Not decision-current"
   * sent the operator down the SED-version path when the actual drift was
   * intent + provider-authority). The SED story is carried by
   * `changedBoundSections` when section comparison applies; `'sed'` appears
   * here only in the whole-document fallback where no section story exists.
   * Empty names with `current: false` is legal — callers keep their generic
   * message as the fallback.
   */
  changedInputs: DecisionAnalysisDriftedInput[];
  /**
   * True when something OTHER than the named bound SED sections drifted
   * (a non-SED input, or a whole-document / cross-version SED comparison with
   * no section story). The automatic-rebind gate (`classifyAutoRebindDrift`)
   * treats a non-current verdict as optional-evidence-only only when this is
   * false and every changed bound section is optional for the domain.
   */
  otherDrift: boolean;
}

/**
 * Piecewise naming of non-SED drift. Mirrors the components of
 * `nonSedPayloadHash` plus the two relaxed comparisons (intent content,
 * like-for-like typed preferences); purely diagnostic — the `current` verdict
 * is computed independently and never reads these names.
 */
function driftedInputNames(
  frozen: DecisionAnalysisInputEnvelopeV1,
  live: DecisionAnalysisInputEnvelopeV1,
  sedChanged: boolean,
  ignoreTestFitSedIdentity = false,
): DecisionAnalysisDriftedInput[] {
  const names: DecisionAnalysisDriftedInput[] = [];
  if (sedChanged) names.push('sed');
  if (!intentCurrent(frozen, live)) names.push('intent');
  if (!typedPreferencesCurrent(frozen, live)) names.push('typed-preferences');
  if (
    (frozen.decisionContext.powerStrategy ?? null)
      !== (live.decisionContext.powerStrategy ?? null)
  ) {
    names.push('power-strategy');
  }
  if (!analysisAuthoritiesCurrent(frozen, live, ignoreTestFitSedIdentity)) {
    names.push('authority');
  }
  if (hashCanonicalJson(frozen.versions) !== hashCanonicalJson(live.versions)) {
    names.push('analysis-versions');
  }
  const upstreamByKind = (
    references: DecisionAnalysisInputEnvelopeV1['upstream'],
  ): Map<string, string> => {
    const grouped = new Map<string, string[]>();
    for (const reference of normalizeDecisionAnalysisUpstreamReferences(references)) {
      const hashes = grouped.get(reference.kind) ?? [];
      hashes.push(hashCanonicalJson({ ...reference, materialIdentity: undefined }));
      grouped.set(reference.kind, hashes);
    }
    return new Map(
      [...grouped.entries()].map(([kind, hashes]) => [kind, hashes.sort().join(' ')]),
    );
  };
  const frozenUpstream = upstreamByKind(frozen.upstream);
  const liveUpstream = upstreamByKind(live.upstream);
  const kinds = [...new Set([...frozenUpstream.keys(), ...liveUpstream.keys()])].sort();
  for (const kind of kinds) {
    if (!upstreamReferencesCurrent(frozen.upstream.filter(reference => reference.kind === kind),
      live.upstream.filter(reference => reference.kind === kind))) {
      names.push(`upstream:${kind}`);
    }
  }
  return names;
}

/**
 * Everything except the SED, typed preferences and intent, hashed.
 *
 * What stays STRICT here — versions, upstream references, authority, domain,
 * siteId, decision context — is deliberate: an analyzer-version bump or an
 * upstream-evidence change is real staleness the UI must surface. Only the
 * three inputs with a documented commit-path relaxation are handled
 * separately: SED (section-scoped), typed preferences (like-for-like
 * projection), and intent (content, below).
 */
function nonSedPayloadHash(input: DecisionAnalysisInputEnvelopeV1): string {
  return hashCanonicalJson({
    ...decisionAnalysisInputHashPayload(input),
    sed: null,
    typedPreferences: null,
    intent: null,
    authority: null,
    upstream: null,
  });
}

/** Only a verified unchanged SED dependency manifest can relax Test Fit's
 * collection provenance. Scenario, model, input/result, layout and boundary
 * identities still match exactly; their computations are never rebound. */
function analysisAuthoritiesCurrent(frozen: DecisionAnalysisInputEnvelopeV1,
  live: DecisionAnalysisInputEnvelopeV1, ignoreSedIdentity: boolean): boolean {
  const project = (input: DecisionAnalysisInputEnvelopeV1): unknown => {
    if (!ignoreSedIdentity || input.domain !== 'test-fit' || input.authority.kind !== 'test-fit') {
      return input.authority;
    }
    const { sedId: _sedId, sedVersion: _sedVersion, ...ref } = input.authority.ref;
    return { kind: 'test-fit', ref };
  };
  return hashCanonicalJson(project(frozen)) === hashCanonicalJson(project(live));
}

/** New semantic proof never upgrades a legacy mismatch. Raw equal refs remain
 * equal when only one side gained the additive metadata; semantic comparison
 * requires compatible stamped proof on BOTH sides and the same exact source. */
function upstreamReferencesCurrent(frozen: readonly DecisionAnalysisUpstreamReferenceV1[],
  live: readonly DecisionAnalysisUpstreamReferenceV1[]): boolean {
  const sort = (refs: readonly DecisionAnalysisUpstreamReferenceV1[]) => [...refs].sort((a, b) =>
    [a.kind, a.id, a.version ?? '', a.contentHash].join('\u0000')
      .localeCompare([b.kind, b.id, b.version ?? '', b.contentHash].join('\u0000')));
  const a = sort(frozen); const b = sort(live);
  return a.length === b.length && a.every((ref, index) => {
    const current = b[index]!;
    if (ref.kind !== current.kind || ref.id !== current.id || ref.version !== current.version) return false;
    if (ref.materialIdentity !== undefined && current.materialIdentity !== undefined) {
      return ref.kind === 'provider-authority'
        && ref.materialIdentity.schemaVersion === current.materialIdentity.schemaVersion
        && ref.materialIdentity.contentHash === current.materialIdentity.contentHash;
    }
    return ref.contentHash === current.contentHash;
  });
}

/**
 * Intent currency by CONTENT, not by revision — the commit path's rule
 * (`analysisInputMatchesLiveSite` compares `hashCanonicalJson(site.projectIntent)`
 * against the bound content hash and never reads the revision). The envelope's
 * `intent.revision` is provenance: a revision bump that reproduces identical
 * intent content must not stale the analysis, exactly as a SED re-key with
 * identical evidence does not.
 */
function intentCurrent(
  frozen: DecisionAnalysisInputEnvelopeV1,
  live: DecisionAnalysisInputEnvelopeV1,
): boolean {
  return frozen.intent.contentHash === live.intent.contentHash;
}

/**
 * Typed-preferences currency, LIKE-FOR-LIKE — the commit path's rule
 * (`analysisInputMatchesLiveSite`, 2026-07-29 custom-brief incident), which the
 * readiness path never received.
 *
 * The analysis binds a deliberately NARROW snapshot — `power-availability` plus
 * the analysed type's own preferences — while the live envelope carries the
 * site's FULL preference map. Hashing both wholesale means any site with a
 * third type's preferences NEVER matches, so every analysis reads stale no
 * matter how stable its actual inputs are. Measured live before this fix:
 * Redhead's supply-chain-risk and water-resource analyses staled with their
 * whole SED content IDENTICAL — the drift was preference keys they never read.
 *
 * A frozen `null` binding means the analysis consumed no preferences, so live
 * preferences cannot stale it (the frozen hash is still verified against its
 * own snapshot). Otherwise the live map is projected onto exactly the bound
 * domains and compared — a change to a bound domain still fails closed.
 */
function typedPreferencesCurrent(
  frozen: DecisionAnalysisInputEnvelopeV1,
  live: DecisionAnalysisInputEnvelopeV1,
): boolean {
  const bound = frozen.typedPreferences.snapshot;
  if (bound == null) {
    return hashCanonicalJson(null) === frozen.typedPreferences.contentHash;
  }
  return boundTypedPreferencesMatch(frozen.domain, bound, live.typedPreferences.snapshot);
}

/**
 * SECTION-SCOPED currency (2026-08-17), mirroring the commit-time predicate
 * `analysisInputMatchesLiveSite` exactly.
 *
 * The whole-envelope hash below projects the SED to its WHOLE-DOCUMENT content
 * hash, so any SED change — a water-provider refresh, a sentiment scan landing —
 * staled every analysis on the site in the readiness path, while commit-time
 * currency had already learned to compare only the sections a domain binds
 * (the 2026-08-10 mutual-staling incident). The two paths disagreeing produced
 * the worst reading: the Path-to-GO hub showed nine "Not decision-current"
 * badges for evidence changes none of those analyses consumed.
 *
 * Staleness must mean "an input THIS analysis used changed". Both envelopes are
 * built by `buildDecisionAnalysisInputEnvelopeV1`, which binds
 * `boundSedSectionsForDomain(domain)` hashes with absent-as-null, so when both
 * sides carry the SAME bound-section key set the comparison is per-section.
 * ANY asymmetry — either side lacking section hashes, or the key sets differing
 * (the binding registry changed between builds) — falls back to whole-document:
 * conservative-stale, never false-current.
 */
export function decisionAnalysisInputDrift(
  frozen: DecisionAnalysisInputEnvelopeV1,
  live: DecisionAnalysisInputEnvelopeV1,
): DecisionAnalysisInputDrift {
  const frozenMaterial = frozen?.sed?.materialDependencies;
  const liveMaterial = live?.sed?.materialDependencies;
  // Malformed present proof cannot fall through to a permissive legacy path.
  if (!frozen?.sed || !live?.sed
    || (frozenMaterial !== undefined && !verifyReportSedMaterialDependencyManifest(frozenMaterial, frozen.domain))
    || (liveMaterial !== undefined && !verifyReportSedMaterialDependencyManifest(liveMaterial, live.domain))
    || (frozenMaterial !== undefined && liveMaterial === undefined)
    || !decisionAnalysisInputEnvelopeV1Schema.safeParse(frozen).success
    || !decisionAnalysisInputEnvelopeV1Schema.safeParse(live).success) {
    return { current: false, changedBoundSections: [], unboundSedChangeOnly: false,
      changedInputs: ['sed'], otherDrift: true };
  }
  if (frozenMaterial && liveMaterial
    && frozen.hashVersion === DECISION_ANALYSIS_HASH_VERSION_V2
    && live.hashVersion === DECISION_ANALYSIS_HASH_VERSION_V2) {
    const materialDrift = reportSedMaterialDependencyDrift(frozenMaterial, liveMaterial);
    const nonSedCurrent = nonSedPayloadHash(frozen) === nonSedPayloadHash(live)
      && typedPreferencesCurrent(frozen, live) && intentCurrent(frozen, live)
      && analysisAuthoritiesCurrent(frozen, live, materialDrift.current)
      && upstreamReferencesCurrent(frozen.upstream, live.upstream);
    const current = materialDrift.current && nonSedCurrent;
    return { current, changedBoundSections: materialDrift.changedFields,
      unboundSedChangeOnly: current && hashDecisionAnalysisInputV1(frozen) !== hashDecisionAnalysisInputV1(live),
      changedInputs: current ? [] : driftedInputNames(frozen, live, false, materialDrift.current),
      otherDrift: !nonSedCurrent };
  }
  const legacyComparablePayload = (input: DecisionAnalysisInputEnvelopeV1, mixed: boolean): unknown => {
    const payload = mixed ? decisionAnalysisCompatInputHashPayload(input) : decisionAnalysisInputHashPayload(input);
    const sed = payload.sed as Record<string, unknown>;
    const { materialDependencies: _materialDependencies, ...legacySed } = sed;
    return { ...payload, sed: legacySed,
      upstream: normalizeDecisionAnalysisUpstreamReferences(input.upstream).map(reference => {
        const { materialIdentity: _materialIdentity, ...legacyReference } = reference;
        return legacyReference;
      }) };
  };
  const same = (a: DecisionAnalysisInputEnvelopeV1, b: DecisionAnalysisInputEnvelopeV1): boolean =>
    hashCanonicalJson(legacyComparablePayload(a, a.hashVersion !== b.hashVersion))
      === hashCanonicalJson(legacyComparablePayload(b, a.hashVersion !== b.hashVersion));

  // Fast path: byte-identical inputs (or cross-version compat equality).
  if (same(frozen, live) && upstreamReferencesCurrent(frozen.upstream, live.upstream)) {
    return {
      current: true,
      changedBoundSections: [],
      unboundSedChangeOnly: false,
      changedInputs: [],
      otherDrift: false,
    };
  }

  // Section scoping applies only when both sides are v2 for the same domain
  // and bind the same section set.
  const frozenSections = frozen.sed.sectionHashes;
  const liveSections = live.sed.sectionHashes;
  const sectionComparable =
    frozen.hashVersion === DECISION_ANALYSIS_HASH_VERSION_V2
    && live.hashVersion === DECISION_ANALYSIS_HASH_VERSION_V2
    && frozen.domain === live.domain
    && frozenSections != null
    && liveSections != null
    && Object.keys(frozenSections).length > 0
    && Object.keys(frozenSections).sort().join('\u0000')
      === Object.keys(liveSections).sort().join('\u0000');

  if (!sectionComparable) {
    // Whole-envelope verdict stands (stale) — no section story to tell, but
    // the piecewise names still say what drifted. SED change detection follows
    // the comparison semantics in force: content hash when both sides are v2,
    // the compat identity (id + version) otherwise.
    const sedChanged =
      frozen.hashVersion === DECISION_ANALYSIS_HASH_VERSION_V2
      && live.hashVersion === DECISION_ANALYSIS_HASH_VERSION_V2
        ? frozen.sed.contentHash !== live.sed.contentHash
        : frozen.sed.id !== live.sed.id || frozen.sed.version !== live.sed.version;
    return {
      current: false,
      changedBoundSections: [],
      unboundSedChangeOnly: false,
      changedInputs: driftedInputNames(frozen, live, sedChanged),
      otherDrift: true,
    };
  }

  // A section is unchanged when any pairing of the schemes both sides carry
  // matches (v2↔v2 keeps every v2 verdict; v3 ignores cache/provenance labels).
  const changedBoundSections = Object.keys(frozenSections)
    .filter(sectionId => !boundSectionUnchanged(
      sectionId,
      { v2: frozenSections, v3: frozen.sed.sectionHashesV3 },
      { v2: liveSections, v3: live.sed.sectionHashesV3 },
    ))
    .sort();
  const nonSedCurrent = nonSedPayloadHash(frozen) === nonSedPayloadHash(live)
    && typedPreferencesCurrent(frozen, live)
    && intentCurrent(frozen, live)
    && analysisAuthoritiesCurrent(frozen, live, false)
    && upstreamReferencesCurrent(frozen.upstream, live.upstream);
  const current = nonSedCurrent && changedBoundSections.length === 0;

  return {
    current,
    changedBoundSections,
    // The envelope hashes differ, nothing this analysis reads changed, and the
    // non-SED inputs are identical — the drift is entirely unbound SED content.
    unboundSedChangeOnly: current,
    // Bound-section drift already tells the SED story here; the names cover
    // only non-SED inputs (sedChanged: false by construction of this branch).
    changedInputs: current ? [] : driftedInputNames(frozen, live, false),
    otherDrift: !nonSedCurrent,
  };
}

export function decisionAnalysisInputsCurrent(
  frozen: DecisionAnalysisInputEnvelopeV1,
  live: DecisionAnalysisInputEnvelopeV1,
): boolean {
  return decisionAnalysisInputDrift(frozen, live).current;
}

export function verifyDecisionAnalysisInputEnvelopeIdentity(
  value: unknown,
): DecisionAnalysisInputIdentityCheck {
  const parsed = decisionAnalysisInputEnvelopeV1Schema.safeParse(value);
  if (!parsed.success) {
    return {
      valid: false,
      input: null,
      inputHash: null,
      reasons: ['Decision-analysis input envelope failed runtime schema validation.'],
    };
  }
  const input = parsed.data;
  const reasons: string[] = [];
  if (input.sed.materialDependencies
    && !verifyReportSedMaterialDependencyManifest(input.sed.materialDependencies, input.domain)) {
    reasons.push('Material SED dependency manifest is invalid for this analysis.');
  }
  if (hashCanonicalJson(input.intent.snapshot) !== input.intent.contentHash) {
    reasons.push('Project-intent snapshot hash does not match its input reference.');
  }
  if (
    hashCanonicalJson(input.typedPreferences.snapshot)
    !== input.typedPreferences.contentHash
  ) {
    reasons.push('Typed-preferences snapshot hash does not match its input reference.');
  }
  return {
    valid: reasons.length === 0,
    input,
    inputHash: hashDecisionAnalysisInputV1(input),
    reasons,
  };
}

export function decisionAnalysisContentHashPayload(input: {
  createdAt: string;
  siteId: string;
  domain: DecisionAnalysisDomain;
  inputHash: string;
  semantic: DecisionAnalysisSemanticContentV1;
}): Record<string, unknown> {
  return {
    schemaVersion: DECISION_ANALYSIS_SCHEMA_VERSION,
    hashVersion: DECISION_ANALYSIS_HASH_VERSION,
    createdAt: input.createdAt,
    siteId: input.siteId,
    domain: input.domain,
    inputHash: input.inputHash,
    semantic: decisionAnalysisSemanticContentV1Schema.parse(input.semantic),
  };
}

export function hashDecisionAnalysisContentV1(input: {
  createdAt: string;
  siteId: string;
  domain: DecisionAnalysisDomain;
  inputHash: string;
  semantic: DecisionAnalysisSemanticContentV1;
}): string {
  return hashCanonicalJson(decisionAnalysisContentHashPayload(input));
}

export function decisionAnalysisIdV1(
  domain: DecisionAnalysisDomain,
  contentHash: string,
): string {
  if (!/^[a-f0-9]{64}$/.test(contentHash)) {
    throw new Error('Decision-analysis content hash must be a SHA-256 hex digest.');
  }
  return `analysis-${domain}-${contentHash.slice(0, 24)}`;
}

export function buildDecisionAnalysisSnapshotV1(
  args: BuildDecisionAnalysisSnapshotV1Args,
): DecisionAnalysisSnapshotV1 {
  const input = decisionAnalysisInputEnvelopeV1Schema.parse(args.input);
  const semantic = decisionAnalysisSemanticContentV1Schema.parse(args.semantic);
  const inputHash = hashDecisionAnalysisInputV1(input);
  const contentHash = hashDecisionAnalysisContentV1({
    createdAt: args.createdAt,
    siteId: input.siteId,
    domain: input.domain,
    inputHash,
    semantic,
  });
  return decisionAnalysisSnapshotV1Schema.parse({
    schemaVersion: DECISION_ANALYSIS_SCHEMA_VERSION,
    // The snapshot's hash scheme IS its input envelope's scheme (the schema
    // enforces the equality) — verifiers recompute under the snapshot's own
    // version, so v1 and v2 snapshots coexist.
    hashVersion: input.hashVersion,
    analysisId: decisionAnalysisIdV1(input.domain, contentHash),
    inputHash,
    contentHash,
    createdAt: args.createdAt,
    siteId: input.siteId,
    domain: input.domain,
    input,
    semantic,
  });
}

/**
 * Verify both levels of immutable identity plus the hashes embedded beside
 * intent and typed-preference snapshots. SED bytes are external to the
 * snapshot, so their content hash is verified when the current input envelope
 * is rebuilt from the live SED.
 */
export function verifyDecisionAnalysisSnapshotIdentity(
  value: unknown,
): DecisionAnalysisSnapshotIdentityCheck {
  const parsed = decisionAnalysisSnapshotV1Schema.safeParse(value);
  if (!parsed.success) {
    return {
      valid: false,
      expectedInputHash: null,
      expectedContentHash: null,
      expectedAnalysisId: null,
      reasons: ['Decision-analysis snapshot failed runtime schema validation.'],
    };
  }

  const snapshot = parsed.data;
  const reasons: string[] = [];
  const inputIdentity = verifyDecisionAnalysisInputEnvelopeIdentity(snapshot.input);
  reasons.push(...inputIdentity.reasons);
  const expectedInputHash = inputIdentity.inputHash
    ?? hashDecisionAnalysisInputV1(snapshot.input);
  if (snapshot.inputHash !== expectedInputHash) {
    reasons.push('Decision-analysis input hash does not match the frozen input envelope.');
  }

  const expectedContentHash = hashDecisionAnalysisContentV1({
    createdAt: snapshot.createdAt,
    siteId: snapshot.siteId,
    domain: snapshot.domain,
    inputHash: snapshot.inputHash,
    semantic: snapshot.semantic,
  });
  if (snapshot.contentHash !== expectedContentHash) {
    reasons.push('Decision-analysis content hash does not match the semantic snapshot.');
  }

  const expectedAnalysisId = decisionAnalysisIdV1(
    snapshot.domain,
    expectedContentHash,
  );
  if (snapshot.analysisId !== expectedAnalysisId) {
    reasons.push('Decision-analysis id does not match the semantic content hash.');
  }

  return {
    valid: reasons.length === 0,
    expectedInputHash,
    expectedContentHash,
    expectedAnalysisId,
    reasons,
  };
}
